import { Module } from 'vuex';
import {
  RootState,
  PaywallPlanObject,
  PaywallPlansInfo,
  ChargebeeInfo,
  Optional,
} from 'frontend/types';

interface PaywallInfo {
  paywall_plans: PaywallPlanObject[] | null;
  paywall_type: string | null;
  nux_goal: string | null;
  nux_skills_needed: string | null;
  can_skip_paywall: boolean;
  site_name: string;
  api_key: string;
  trial_length: number;
}

function initialState(): PaywallInfo {
  return {
    paywall_plans: null,
    paywall_type: null,
    nux_goal: null,
    nux_skills_needed: null,
    can_skip_paywall: true,
    site_name: '',
    api_key: '',
    trial_length: 7,
  };
}

const paywall: Module<PaywallInfo, RootState> = {
  namespaced: true,
  state: initialState(),

  mutations: {
    setValue(state: object, value: object): void {
      Object.assign(state, value);
    },
  },

  actions: {
    clearPlans({ commit }) {
      commit('setValue', initialState());
    },
  },

  getters: {
    canSkipPaywall(state): boolean {
      return state.can_skip_paywall;
    },
    getChargebeeInfo(state): ChargebeeInfo {
      return { siteName: state.site_name, apiKey: state.api_key };
    },
    hasPaywallPlans(state): boolean {
      return !!state.paywall_plans;
    },
    paywallPlans(state): PaywallPlansInfo {
      const plans: PaywallPlansInfo = {};
      if (state.paywall_plans) {
        for (const plan of state.paywall_plans) {
          plan.is_popular = false;
          switch (plan.interval) {
            case 'year':
              if (plan.is_group) {
                break;
              }
              if (plan.is_math_foundations) {
                plans.annualMathFoundationsPlan = plan;
                break;
              }
              plans.annualPlan = plan;
              if (plan.is_trial) {
                plans.annualPlanTrial = plan;
                plans.annualPlanIsTrialPlan = true;
              } else {
                plans.annualPlanDirect = plan;
              }
              // eslint-disable-next-line camelcase
              if (plans.annualPlan?.includes_referral_discount) {
                plans.annualPlanIncludesReferralDiscount = true;
              }
              break;
            case '3month':
              plans.threeMonthPlan = plan;
              break;
            case '6month':
              plans.sixMonthPlan = plan;
              break;
            case 'month':
              plans.monthlyPlan = plan;
              break;
            case '2year':
              plans.twoYearPlan = plan;
              break;
            case '3year':
              plans.threeYearPlan = plan;
              break;
            case '4year':
              plans.fourYearPlan = plan;
              break;
          }

          if (plan.display_type === 'group_year') {
            plans.groupPlan = plan;
          }

          if (plan.duration === 'lifetime') {
            plans.lifetimePlan = plan;
          }
        }
      }

      if (plans.annualPlan) {
        plans.annualPlan.is_popular = true;
      } else if (plans.sixMonthPlan) {
        plans.sixMonthPlan.is_popular = true;
      } else if (plans.threeMonthPlan) {
        plans.threeMonthPlan.is_popular = true;
      } else if (plans.monthlyPlan) {
        plans.monthlyPlan.is_popular = true;
      }
      return plans;
    },
    trialLength(state): number {
      return state.trial_length;
    },
  },
};

interface PrepurchaseDestinationUrl {
  value?: string;
}

export const prepurchaseDestinationUrl: Module<PrepurchaseDestinationUrl, RootState> = {
  namespaced: true,
  state: {
    value: undefined,
  },
  getters: {
    prepurchaseDestinationUrl: (state: PrepurchaseDestinationUrl): Optional<string> => state.value,
  },
  mutations: {
    setValue(state: PrepurchaseDestinationUrl, value: string): void {
      state.value = value;
    },
  },
};

export default paywall;
